import { Box, Collapse, Fab, IconButton, Tooltip } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { IoColorPalette, IoMoon, IoSunny } from 'react-icons/io5'

import { useTheme } from '@/contexts'

const colorOptions = [
  { value: 'default', label: 'padrão', color: '#1976d2' },
  { value: 'green', label: 'verde', color: '#4caf50' },
  { value: 'blue', label: 'azul', color: '#2196f3' },
  { value: 'pink', label: 'rosa', color: '#e91e63' },
  { value: 'purple', label: 'roxo', color: '#9c27b0' },
]

interface ThemeSelectorProps {
  y?: 'top' | 'bottom'
  x?: 'left' | 'right'
}

function ThemeSelector({ x = 'right', y = 'bottom' }: ThemeSelectorProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false)
  const { colorTheme, setColorTheme, isDark, toggleDarkMode } = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (isOpen && !isTransitioning) {
          setIsOpen(false)
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isOpen, isTransitioning])

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isTransitioning) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, isTransitioning])

  const getPosition = () => {
    const positions = {
      top: { top: 8 },
      bottom: { bottom: 8 },
      left: { left: 8 },
      right: { right: 8 },
    }

    return {
      ...positions[y],
      ...positions[x],
    }
  }

  const handleColorSelect = (color: string) => {
    setIsTransitioning(true)
    setColorTheme(color as any)

    setTimeout(() => {
      setIsTransitioning(false)
      setIsOpen(false)
    }, 300)
  }

  const handleDarkModeToggle = () => {
    setIsTransitioning(true)
    toggleDarkMode()
    setIsOpen(false)

    setTimeout(() => {
      setIsTransitioning(false)
    }, 300)
  }

  const handleFabClick = () => {
    if (!isTransitioning) {
      setIsOpen(!isOpen)
    }
  }

  const tooltipPlacement = x === 'right' ? 'left' : 'right'

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        zIndex: 998,
        ...getPosition(),
        transform: 'scale(0)',
        animation: 'fabEnter 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
        '@keyframes fabEnter': {
          '0%': {
            transform: 'scale(0) rotate(-180deg)',
            opacity: 0,
          },
          '100%': {
            transform: 'scale(1) rotate(0deg)',
            opacity: 1,
          },
        },
      }}>
      <Collapse in={isOpen} timeout={300}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: y === 'bottom' ? 'column-reverse' : 'column',
            gap: 1,
            mb: y === 'bottom' ? 1 : 0,
            mt: y === 'top' ? 1 : 0,
            alignItems: 'center',
          }}>
          <Tooltip
            title={`Trocar modo para ${isDark ? 'claro' : 'escuro'}`}
            placement={tooltipPlacement}>
            <IconButton
              onClick={handleDarkModeToggle}
              disabled={isTransitioning}
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                animation:
                  'optionEnter 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.1s both',
                '&:hover': {
                  transform: 'scale(1.1)',
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
                '@keyframes optionEnter': {
                  '0%': {
                    transform: 'scale(0) rotate(-90deg)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'scale(1) rotate(0deg)',
                    opacity: 1,
                  },
                },
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 0.3s ease',
                  transform: isTransitioning ? 'rotate(180deg)' : 'rotate(0deg)',
                }}>
                {isDark ? <IoSunny size={20} /> : <IoMoon size={20} />}
              </Box>
            </IconButton>
          </Tooltip>

          {colorOptions.map((option, index) => (
            <Tooltip
              key={option.value}
              title={`Tema ${option.label}`}
              placement={tooltipPlacement}>
              <IconButton
                onClick={() => handleColorSelect(option.value)}
                disabled={isTransitioning}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: option.color,
                  border: colorTheme === option.value ? '3px solid' : '1px solid',
                  borderColor:
                    colorTheme === option.value
                      ? 'background.paper'
                      : 'rgba(255, 255, 255, 0.3)',
                  boxShadow: colorTheme === option.value ? 3 : 2,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `optionEnter 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${(index + 2) * 0.1}s both`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    bgcolor: option.color,
                    opacity: 0.8,
                    transform: 'scale(1.1)',
                    boxShadow: colorTheme === option.value ? 4 : 3,
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                  '&::after':
                    colorTheme === option.value
                      ? {
                          content: '""',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '12px',
                          height: '12px',
                          bgcolor: 'background.paper',
                          borderRadius: '50%',
                          animation: 'checkmark 0.3s ease-in-out',
                          '@keyframes checkmark': {
                            '0%': {
                              transform: 'translate(-50%, -50%) scale(0)',
                              opacity: 0,
                            },
                            '100%': {
                              transform: 'translate(-50%, -50%) scale(1)',
                              opacity: 1,
                            },
                          },
                        }
                      : {},
                  '@keyframes optionEnter': {
                    '0%': {
                      transform: 'scale(0) rotate(-180deg)',
                      opacity: 0,
                    },
                    '100%': {
                      transform: 'scale(1) rotate(0deg)',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Collapse>

      <Fab
        onClick={handleFabClick}
        size="medium"
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          border: '1px solid',
          borderColor: isOpen ? 'primary.main' : 'divider',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          position: 'relative',
          '&:hover': {
            transform: 'scale(1.1)',
            bgcolor: 'background.paper',
            borderColor: 'primary.main',
            '& .fab-icon': {
              transform: 'rotate(10deg) scale(1.05)',
            },
          },
          '&:active': {
            transform: 'scale(0.95)',
            '& .fab-icon': {
              transform: 'rotate(0deg) scale(0.9)',
            },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(45deg, ${colorOptions.find((c) => c.value === colorTheme)?.color || '#1976d2'}, transparent)`,
            opacity: isOpen ? 0.1 : 0,
            transition: 'opacity 0.3s ease',
            borderRadius: '50%',
          },
        }}>
        <Box
          className="fab-icon"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isOpen ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1)',
            color: isOpen ? 'primary.main' : 'text.primary',
          }}>
          <IoColorPalette size={22} />
        </Box>
      </Fab>
    </Box>
  )
}

export default ThemeSelector
